import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { ISignInProvider } from '../../../data/types';
import { Column } from '../../layout/Column';
import { Brand } from '../../shared/Brand';
import { Card } from '../../shared/card/Card';
import { CardBody } from '../../shared/card/CardBody';
import { CardHeader } from '../../shared/card/CardHeader';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { LoginFooter } from './components/LoginFooter';
import { LoginStepper } from './components/LoginStepper';
import { LoginStepRenderer } from './components/LoginStepRenderer';
import { createSubmissionHandlers } from './helpers/loginHandlers';
import { useLoginStateMachine } from './hooks/useLoginStateMachine';

// eslint-disable-next-line no-shadow
export enum LoginState {
  IDENTIFY,
  SELECT_TENANT,
  SELECT_PROVIDER,
  AUTHENTICATE,
  FORGOT_PASSWORD,
  SECOND_FACTOR_REQUIRED,
  VERIFYING_DEVICE_FOR_MFA,
  SECOND_FACTOR_VERIFICATION,
  UNABLE_TO_SEND_VERIFICATION,
  SSO_AUTH,
  SELECT_ORGANIZATION,
}

const LoginComponent = (props: RouteComponentProps): React.ReactElement => {
  // Use custom hook for all state management
  const {
    email,
    password,
    error,
    verificationCode,
    loading,
    showResendSuccess,
    resendCooldown,
    resetSent,
    multiFactorHints,
    selectedFactor,
    selectedProvider,
    selectedSSOMethod,
    selectedTenant,
    selectedOrganization,
    loginState,
    setEmail,
    setPassword,
    setError,
    setVerificationCode,
    setLoading,
    setShowResendSuccess,
    setResendCooldown,
    setResetSent,
    setMultiFactorHints,
    setSelectedFactor,
    setSelectedProvider,
    setSelectedSSOMethod,
    setSelectedTenant,
    setSelectedOrganization,
    auth,
    resolver,
    verificationId,
    userInfo,
    recaptchaVerifier,
    nextState,
    sendMultiFactorCode,
  } = useLoginStateMachine();

  const submissionHandlers = React.useMemo(
    (): Record<LoginState, () => Promise<void>> =>
      createSubmissionHandlers({
        // State values
        email,
        password,
        verificationCode,
        selectedFactor,
        selectedProvider,
        selectedSSOMethod,
        selectedTenant,
        selectedOrganization,

        // State setters
        setError,
        setLoading,
        setMultiFactorHints,
        setResetSent,

        // Refs
        auth,
        userInfo,
        resolver,
        verificationId,
        recaptchaVerifier,

        // Functions
        nextState,
        sendMultiFactorCode,

        // Router
        history: props.history,
      }),
    [
      email,
      password,
      verificationCode,
      selectedFactor,
      selectedProvider,
      selectedSSOMethod,
      selectedTenant,
      selectedOrganization,
      props.history,
      setError,
      setLoading,
      setMultiFactorHints,
      setResetSent,
      userInfo,
      resolver,
      verificationId,
      recaptchaVerifier,
      nextState,
      sendMultiFactorCode,
    ]
  );

  /**
   * @description handles the user submitting form
   */
  const onSubmit = async (
    e: React.MouseEvent | React.FormEvent
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    const handler = submissionHandlers[loginState];
    if (handler) {
      await handler();
    }
  };

  return (
    <LoginContainer action='#' onSubmit={onSubmit}>
      <StyledCard>
        <Column>
          <CardHeader surface={8} style={{ justifyContent: 'center' }}>
            <Brand role='presentation' />
          </CardHeader>
          {loading && <LinearLoading />}
          <LoginStepper
            currentState={loginState}
            hasTenants={(userInfo.current?.tenants?.length ?? 0) > 1}
            hasMultipleProviders={((): boolean => {
              const providers = userInfo.current?.sign_in_providers ?? [];
              const hasPassword = providers.some(
                (p: ISignInProvider): boolean => p.type === 'password'
              );
              const ssoProviders = providers.filter(
                (p: ISignInProvider): boolean =>
                  p.type === 'oidc' || p.type === 'saml'
              );
              return (
                (hasPassword && ssoProviders.length > 0) ||
                ssoProviders.length > 1
              );
            })()}
          />
          <CardBody
            notScrollable
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '200px',
            }}
          >
            <LoginStepRenderer
              loginState={loginState}
              email={email}
              password={password}
              error={error}
              verificationCode={verificationCode}
              showResendSuccess={showResendSuccess}
              resetSent={resetSent}
              multiFactorHints={multiFactorHints}
              selectedFactor={selectedFactor}
              selectedProvider={selectedProvider}
              selectedSSOMethod={selectedSSOMethod}
              selectedTenant={selectedTenant}
              selectedOrganization={selectedOrganization}
              signInProviders={userInfo.current?.sign_in_providers ?? []}
              userTenants={userInfo.current?.tenants ?? []}
              userOrganizations={userInfo.current?.organizations ?? []}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onVerificationCodeChange={setVerificationCode}
              onSelectFactor={setSelectedFactor}
              onSelectProvider={setSelectedProvider}
              onSelectSSOMethod={setSelectedSSOMethod}
              onSelectTenant={setSelectedTenant}
              onSelectOrganization={setSelectedOrganization}
              onStartOver={(): void => nextState(LoginState.IDENTIFY)}
              onForgotPassword={(): void =>
                nextState(LoginState.FORGOT_PASSWORD)
              }
            />
          </CardBody>
          <LoginFooter
            loginState={loginState}
            email={email}
            password={password}
            verificationCode={verificationCode}
            resendCooldown={resendCooldown}
            resetSent={resetSent}
            selectedFactor={selectedFactor}
            selectedProvider={selectedProvider}
            selectedSSOMethod={selectedSSOMethod}
            selectedOrganization={selectedOrganization}
            selectedTenant={selectedTenant}
            onBack={(): void => nextState(LoginState.IDENTIFY)}
            onResendCode={sendMultiFactorCode}
            setResendCooldown={setResendCooldown}
            setShowResendSuccess={setShowResendSuccess}
          />
        </Column>
      </StyledCard>
    </LoginContainer>
  );
};

export const Login = withRouter(LoginComponent);

export const LoginContainer = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 600px;
  flex: 1;
  @media (max-width: 991px) {
    max-width: 340px;
  }
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 600px;
  max-height: 500px;
  margin-top: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
  transition: box-shadow 300ms ease-in-out;

  &:hover {
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 991px) {
    max-width: 340px;
  }
`;
